import { useCallback, useEffect, useContext, useState } from "react"
import { DateTime } from "luxon"

//context
import { UserContext } from "../../../../../context/userContext"

//Referrals
import { refApiReferralEntityChangeLogGet } from "../../../partnerAPI"
import ReferralEntityFormChangeLogFilter from "./ReferralEntityFormChangeLogFilter"

//Styles
import myStyles from "../../../../../styles/referrals/ReferralEntityFormChangeLog.module.css"

//SOAR
import { LoaderMedium } from "../../../../../components/Loaders"
import { LogError } from "../../../../../helpers/logger"

//PrimeReact
import { Accordion, AccordionTab } from "primereact/accordion"
import ReferralEntityChangeLogItem from "./ReferralEntityChangeLogItem"

//GrahQL
import { GET_USERS_FROM_LIST } from "../../queries"
import { useLazyQuery } from "@apollo/client"

export default function ReferralEntityFormChangeLog({
  referralEntityId,
  changeLog,
  onSetChangeLog,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState({
    category: "",
    field: undefined,
    createdFrom: DateTime.now().plus({ months: -1 }).toJSDate(),
    createdTo: DateTime.now().toJSDate(),
    includeFilterLists: true,
  })
  const [staffList, setStaffList] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [fields, setFields] = useState([])

  const [getStaff] = useLazyQuery(GET_USERS_FROM_LIST, {
    fetchPolicy: "cache-and-network",
  })

  const getChangeLog = useCallback(async () => {
    setIsLoading(true)

    const convertDateToUtcIso = (date, eop) => {
      let luxDate = DateTime.fromJSDate(date)

      if (eop === true) {
        luxDate = luxDate.endOf("day")
      } else {
        luxDate = luxDate.startOf("day")
      }

      const utcDate = luxDate.toUTC().toISO()

      return utcDate
    }

    try {
      const payload = {
        referralEntityId: referralEntityId,
        category: filters.category,
        field: filters.field,
        organizationId: filters.organizationId,
        userId: filters.userId,
        from: convertDateToUtcIso(filters.createdFrom),
        to: convertDateToUtcIso(filters.createdTo, true),
        includeFilterLists: filters.includeFilterLists,
      }
      const logs = await refApiReferralEntityChangeLogGet(
        userCtx.tokens.getMainToken(),
        payload,
      )

      let userList = staffList
      let organizationList = organizations

      if (logs?.users?.length > 0) {
        userList = logs.users
        setStaffList(userList)
      }

      if (logs?.organizations?.length > 0) {
        organizationList = logs.organizations
        setOrganizations(organizationList)
      }

      if (logs?.fields?.length > 0) {
        setFields(
          logs.fields.map((field) => {
            let name = ""

            switch (field.sourceName) {
              case "ReferralEntity":
                name = `Partner profile: ${field.fieldName}`
                break
              case "ReferralLocation":
                name = `Referral location: ${field.fieldName}`
                break
              case "ReferralLocationServiceType":
                name = "Referral location services"
                break
              default:
                name = `${field.sourceName}: ${field.fieldName}`
                break
            }

            return { ...field, displayName: name }
          }),
        )
      }

      if (logs.changes?.length > 0) {
        logs.changes.forEach((entry) => {
          const staff = userList.filter((user) => user.id === entry.userId)

          if (staff.length > 0) {
            entry.userName = staff[0].fullName
          } else {
            entry.userName = "Unknown"
          }

          const org = organizationList.filter(
            (organization) => organization.id === entry.organizationId,
          )

          if (org.length > 0) {
            entry.organizationName = org[0].name
          } else {
            entry.organizationName = "Unknown"
          }
        })

        onSetChangeLog(logs.changes)
        setIsLoading(false)
      } else {
        onSetChangeLog(undefined)
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to retrieve change log", error)
      userCtx.setNotificationError("Unable to retrieve change log")

      onSetChangeLog(undefined)
      setIsLoading(false)
    }

    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralEntityId, userCtx.tokens.getMainToken, filters])

  useEffect(() => {
    getChangeLog()
  }, [getChangeLog])

  return (
    <div>
      <Accordion className={myStyles.filterContainer}>
        <AccordionTab header={"Filters"}>
          <ReferralEntityFormChangeLogFilter
            activeFilters={filters}
            setActiveFilters={setFilters}
            users={staffList}
            organizations={organizations}
            fields={fields}
          />
        </AccordionTab>
      </Accordion>
      {isLoading ? <LoaderMedium /> : null}

      {!!changeLog && !isLoading ? (
        changeLog.map((log, index) => (
          <ReferralEntityChangeLogItem
            logEntry={log}
            key={index}
            staffList={staffList}
            onSaveStaffEntry={(staffEntry) => {
              setStaffList([...staffList, staffEntry])
            }}
          />
        ))
      ) : (
        <>No logs found</>
      )}
    </div>
  )
}
